import type { StockAssetEntity, StockBalanceEntity, StockBatchEntity, StockTransactionEntity, StockTransactionItemEntity, AssetType, TransactionStatus, TransactionType } from '@/features/stock/domain/entities/StockEntity.js';
import type { IStockRepository, AcceptTransactionInput, RejectTransactionInput, AssetCreateInput, AssetHistoryOptions, AssetHistoryPage, AssetHistoryPageOptions, AssetListOptions, AssetUpdateInput, BalanceListOptions, BalanceExportOptions, BatchesAtWarehouseOptions, BatchesByAssetOptions, BalancePageOptions, TransactionExportOptions, TransactionPageOptions, TransactionPage, TransactionListOptions, TransactionCreateInput, TransactionUpdateInput } from '@/features/stock/domain/repositories/IStockRepository.js';
import { StockRemoteDataSource } from '@/features/stock/data/datasources/StockRemoteDataSource.js';
import type { StockAssetDto, StockBalanceDto, StockBatchDto, StockTransactionDto, StockTransactionItemDto } from '@/features/stock/data/dtos/StockDto.js';

function mapAsset(d: StockAssetDto): StockAssetEntity {
  return {
    id: d.id, orgId: d.org_id, projectId: d.project_id,
    available: d.available, name: d.name, sku: d.sku,
    type: (d.type as AssetType) ?? 'other', category: d.category ?? null,
    unit: d.unit ?? 'gói', pack: d.pack ?? 'thùng', bundle: d.bundle ?? 'lốc',
    unitPerBundle: d.unit_per_bundle ?? 6, unitPerPack: d.unit_per_pack ?? 24,
    defaultUnitPerSoldItem: d.default_unit_per_sold_item ?? 1,
    unitPrice: Number(d.unit_price ?? 0), bundlePrice: Number(d.bundle_price ?? 0), packPrice: Number(d.pack_price ?? 0),
    description: d.description ?? null, tags: d.tags ?? [],
    mfgDate: d.mfg_date ?? null, expDate: d.exp_date ?? null,
    createdAt: d.created_at, updatedAt: d.updated_at,
    totalQty: Number(d.total_unit_qty ?? 0),
    totalShrinkage: Number(d.total_shrinkage_unit_qty ?? 0),
  };
}

function mapBalance(d: StockBalanceDto): StockBalanceEntity {
  return {
    id: d.id, orgId: d.org_id, projectId: d.project_id,
    warehouseId: d.warehouse_id, warehouseName: d.warehouse_location?.name ?? null,
    warehouseProvinceCode: d.warehouse_location?.province_code ?? null,
    warehouseAddress: d.warehouse_location?.full_address_cache ?? null,
    assetId: d.asset_id, unitQty: d.unit_qty ?? 0, bookedUnitQty: d.booked_unit_qty ?? 0,
    reservedOutQty: Number(d.reserved_out_qty ?? 0), expectedInQty: Number(d.expected_in_qty ?? 0), availableQty: Number(d.available_qty ?? d.unit_qty ?? 0),
    inboundUnitQty: d.inbound_unit_qty ?? 0, outboundUnitQty: d.outbound_unit_qty ?? 0,
    shrinkageUnitQty: d.shrinkage_unit_qty ?? 0, updatedAt: d.updated_at,
  };
}

function mapTx(d: StockTransactionDto): StockTransactionEntity {
  return {
    id: d.id, orgId: d.org_id, projectId: d.project_id,
    type: d.type as TransactionType, status: d.status as TransactionStatus,
    createdByUserId: d.created_by_user_id, confirmedByUserId: d.confirmed_by_user_id ?? null,
    confirmedAt: d.confirmed_at ?? null,
    fromWarehouseId: d.from_warehouse_id ?? null, fromWarehouseName: d.from_location?.name ?? null,
    toWarehouseId: d.to_warehouse_id ?? null, toWarehouseName: d.to_location?.name ?? null,
    adjustmentDirection: d.adjustment_direction, relatedTransactionId: d.related_transaction_id,
    rejectReason: d.reject_reason, dispatchedAt: d.dispatched_at, rejectedAt: d.rejected_at,
    actorNames: d.actor_names, audit: d.audit, corrections: d.corrections,
    notes: d.notes ?? null, metadata: d.metadata ?? null,
    photos: d.photos ?? [],
    items: d.stock_transaction_items ? d.stock_transaction_items.map(mapTxItem) : undefined,
    createdAt: d.created_at, updatedAt: d.updated_at,
  };
}

function mapTxItem(d: StockTransactionItemDto): StockTransactionItemEntity {
  const link = Array.isArray(d.batch_code_link) ? d.batch_code_link[0] : d.batch_code_link;
  const metadata = d.batch_code_link !== undefined ? link?.batch_metadata : d.batch_metadata;
  return {
    id: d.id, transactionId: d.transaction_id,
    orgId: d.org_id, projectId: d.project_id,
    assetId: d.asset_id, sourceBatchId: d.source_batch_id ?? null,
    batchCodeId: (d.batch_code_link !== undefined ? link?.batch_code_id : d.batch_code_id) ?? null,
    batchCode: metadata?.batch_code ?? null,
    mfgDate: metadata?.mfg_date ?? null,
    expDate: metadata?.exp_date ?? null,
    unitQty: d.unit_qty ?? 0, confirmedUnitQty: d.confirmed_unit_qty ?? 0,
    shrinkageUnitQty: d.shrinkage_unit_qty ?? 0, shrinkageReason: d.shrinkage_reason ?? null,
    createdAt: d.created_at, updatedAt: d.updated_at,
  };
}

function mapBatch(d: StockBatchDto): StockBatchEntity {
  return {
    id: d.id, orgId: d.org_id, projectId: d.project_id,
    assetId: d.asset_id, locationId: d.location_id,
    originId: d.origin_id, originTxId: d.origin_tx_id ?? null,
    batchCodeId: d.batch_code_id ?? null,
    batchCode: d.batch_metadata?.batch_code ?? null,
    mfgDate: d.batch_metadata?.mfg_date ?? null,
    expDate: d.batch_metadata?.exp_date ?? null,
    qty: d.qty ?? 0, bookedQty: d.booked_qty ?? 0,
    receivedQty: d.received_qty, receivedAt: d.received_at, createdAt: d.created_at,
  };
}

export class StockRepository implements IStockRepository {
  constructor(private readonly ds: StockRemoteDataSource) {}

  async listAssetFlow(options: import('@/features/stock/domain/repositories/IStockRepository.js').AssetFlowOptions) {return (await this.ds.listAssetFlow(options)).map(mapTx);}
  async listBalancePage(options: BalancePageOptions) {const page=await this.ds.listBalancePage(options);return {...page,items:page.items.map(mapBalance)};}
  async listBalanceExport(options: BalanceExportOptions): Promise<StockBalanceEntity[]> {
    return (await this.ds.listBalanceExport(options)).map(mapBalance);
  }
  async getTransaction(id: string) { const d = await this.ds.getTransaction(id); return d ? mapTx(d) : null; }
  async listTransactionPage(options: TransactionPageOptions): Promise<TransactionPage> {
    const page = await this.ds.listTransactionPage(options);
    return { ...page, items: page.items.map(mapTx) };
  }
  async listTransactionExport(options: TransactionExportOptions): Promise<StockTransactionEntity[]> {
    return (await this.ds.listTransactionExport(options)).map(mapTx);
  }
  async transitionTransaction(id: string, status: 'delivering' | 'cancelled', reason?: string, photos?: string[]) {
    const d = await this.ds.transitionTransaction(id, status, reason, photos); return d ? mapTx(d) : null;
  }
  async listAssets(options: AssetListOptions): Promise<StockAssetEntity[]> {
    return (await this.ds.listAssets(options)).map(mapAsset);
  }

  async getAssetById(id: string): Promise<StockAssetEntity | null> {
    const d = await this.ds.getAssetById(id);
    return d ? mapAsset(d) : null;
  }

  async createAsset(input: AssetCreateInput): Promise<StockAssetEntity> {
    const d = await this.ds.createAsset({
      org_id: input.orgId, project_id: input.projectId,
      name: input.name, sku: input.sku,
      available: input.available ?? true,
      type: input.type ?? 'other', category: input.category ?? null,
      unit: input.unit ?? 'gói', pack: input.pack ?? 'thùng', bundle: input.bundle ?? 'lốc',
      unit_per_bundle: input.unitPerBundle ?? 6, unit_per_pack: input.unitPerPack ?? 24,
      default_unit_per_sold_item: input.defaultUnitPerSoldItem ?? 1,
      unit_price: input.unitPrice ?? 0, bundle_price: input.bundlePrice ?? 0, pack_price: input.packPrice ?? 0,
      description: input.description ?? null, tags: input.tags ?? [],
      ...(input.mfgDate !== undefined ? { mfg_date: input.mfgDate } : {}),
      ...(input.expDate !== undefined ? { exp_date: input.expDate } : {}),
    });
    if (!d) throw new Error('Asset creation failed');
    return mapAsset(d);
  }

  async updateAsset(id: string, input: AssetUpdateInput): Promise<StockAssetEntity | null> {
    const p: Partial<StockAssetDto> = {};
    if (input.available !== undefined) p.available = input.available;
    if (input.name !== undefined) p.name = input.name;
    if (input.sku !== undefined) p.sku = input.sku;
    if (input.type !== undefined) p.type = input.type;
    if (input.category !== undefined) p.category = input.category;
    if (input.unit !== undefined) p.unit = input.unit;
    if (input.pack !== undefined) p.pack = input.pack;
    if (input.bundle !== undefined) p.bundle = input.bundle;
    if (input.unitPerBundle !== undefined) p.unit_per_bundle = input.unitPerBundle;
    if (input.unitPerPack !== undefined) p.unit_per_pack = input.unitPerPack;
    if (input.defaultUnitPerSoldItem !== undefined) p.default_unit_per_sold_item = input.defaultUnitPerSoldItem;
    if (input.unitPrice !== undefined) p.unit_price = input.unitPrice;
    if (input.bundlePrice !== undefined) p.bundle_price = input.bundlePrice;
    if (input.packPrice !== undefined) p.pack_price = input.packPrice;
    if (input.description !== undefined) p.description = input.description;
    if (input.tags !== undefined) p.tags = input.tags;
    if (input.mfgDate !== undefined) p.mfg_date = input.mfgDate;
    if (input.expDate !== undefined) p.exp_date = input.expDate;
    const d = await this.ds.updateAsset(id, p);
    return d ? mapAsset(d) : null;
  }

  async deleteAsset(id: string): Promise<void> {
    await this.ds.deleteAsset(id);
  }

  async listBalances(options: BalanceListOptions): Promise<StockBalanceEntity[]> {
    return (await this.ds.listBalances(options)).map(mapBalance);
  }

  async listBatchesAtWarehouse(options: BatchesAtWarehouseOptions): Promise<StockBatchEntity[]> {
    return (await this.ds.listBatchesAtWarehouse(options)).map(mapBatch);
  }

  async listBatchesByAsset(options: BatchesByAssetOptions): Promise<StockBatchEntity[]> {
    return (await this.ds.listBatchesByAsset(options)).map(mapBatch);
  }

  async listTransactions(options: TransactionListOptions): Promise<StockTransactionEntity[]> {
    return (await this.ds.listTransactions(options)).map(mapTx);
  }

  async listAssetHistory(options: AssetHistoryOptions): Promise<StockTransactionEntity[]> {
    return (await this.ds.listAssetHistory(options)).map(mapTx);
  }

  async listAssetHistoryPage(options: AssetHistoryPageOptions): Promise<AssetHistoryPage> {
    const page = await this.ds.listAssetHistoryPage(options);
    return {
      items: page.items.map(mapTx),
      nextCursor: page.nextCursor
        ? { createdAt: page.nextCursor.created_at, id: page.nextCursor.id }
        : null,
    };
  }

  async createTransaction(input: TransactionCreateInput): Promise<StockTransactionEntity> {
    const d = await this.ds.createTransaction(input);
    if (!d) throw new Error('Không tạo được phiếu');
    return mapTx(d);
  }
  async updateTransaction(id: string, input: TransactionUpdateInput): Promise<StockTransactionEntity | null> {
    const d = await this.ds.updateTransaction(id, input);
    return d ? mapTx(d) : null;
  }

  async acceptTransaction(input: AcceptTransactionInput): Promise<StockTransactionEntity | null> {
    const d = await this.ds.acceptTransaction(input.id, input.items.map((it) => ({
      ...(it.id !== undefined ? { id: it.id } : {}),
      asset_id: it.assetId,
      source_batch_id: it.sourceBatchId ?? null,
      batch_code_id: it.batchCodeId ?? null,
      unit_qty: it.unitQty,
      confirmed_unit_qty: it.confirmedUnitQty,
      shrinkage_unit_qty: it.shrinkageUnitQty,
      shrinkage_reason: it.shrinkageReason ?? null,
    })), input.photos ?? []);
    return d ? mapTx(d) : null;
  }

  async rejectTransaction(input: RejectTransactionInput): Promise<StockTransactionEntity | null> {
    const d = await this.ds.rejectTransaction(input.id, input.reason, input.photos ?? []);
    return d ? mapTx(d) : null;
  }

  async deleteTransaction(id: string): Promise<void> {
    await this.ds.deleteTransaction(id);
  }

  async listTransactionItems(transactionId: string): Promise<StockTransactionItemEntity[]> {
    return (await this.ds.listTransactionItems(transactionId)).map(mapTxItem);
  }

  async recalcWarehouse(orgId: string, projectId: string, warehouseId: string): Promise<void> {
    await this.ds.recalcWarehouse(orgId, projectId, warehouseId);
  }

  async recalcProject(orgId: string, projectId: string): Promise<void> {
    await this.ds.recalcProject(orgId, projectId);
  }
}
